import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import { WebSocketComponent } from "components";
import { useTypedQuery } from "hooks/useTypedQuery";

import type { QueryKey, UseQueryOptions } from "@tanstack/react-query";
import type { WebSocketMessageListener } from "components";
import type {
  TypeOfBackendTypedQueryKey,
  TypeOfPermanentBackendTypedQueryKey,
  TypedBackendQueryKey,
  TypedPermanentBackendQueryKey,
  UndefinedInitialBackendDataTypedOptions,
  UndefinedInitialPermanentBackendDataTypedOptions
} from "hooks/useTypedQuery";

// DEPRECATED: TO BE REMOVED WHEN we replaced all usage with UndefinedInitialDataTypedOptionsWithListeners<K>
interface ExtendedUseQueryOptions<TQueryFnData, TError = Error, TData = TQueryFnData, TQueryKey extends QueryKey = QueryKey>
  extends UseQueryOptions<TQueryFnData, TError, TData, TQueryKey> {
  listeners: WebSocketMessageListener<unknown>[];
}

// DEPRECATED: TO BE REMOVED WHEN we replaced all usage with useRealTimeTypedQuery/usePermanentRealTimeTypedQuery
export const useRealTimeQuery = <TQueryFnData, TError = Error, TData = TQueryFnData, TQueryKey extends QueryKey = QueryKey>(
  options: ExtendedUseQueryOptions<TQueryFnData, TError, TData, TQueryKey>
) => {
  const { listeners, ...restOptions } = options;
  const query = useQuery({ ...restOptions });

  useEffect(() => {
    if (listeners.length < 1 || !query.isFetched) return;

    const removeMessageListeners: (() => void)[] = [];

    listeners.forEach(listener => {
      const removeMessageListener = WebSocketComponent.addMessageListener(listener);
      removeMessageListeners.push(removeMessageListener);
    });

    return () => {
      removeMessageListeners.forEach(removeMessageListener => removeMessageListener());
    };
  }, [listeners, query.isFetched]);

  return query;
};

type UndefinedInitialDataTypedOptionsWithListeners<K, R> = UndefinedInitialBackendDataTypedOptions<K, R> & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  listeners: WebSocketMessageListener<any>[];
};

type PermanentUndefinedInitialDataTypedOptionsWithListeners<K, R> = UndefinedInitialPermanentBackendDataTypedOptions<K, R> & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  listeners: WebSocketMessageListener<any>[];
};

export const useRealTimeTypedQuery = <K extends TypedBackendQueryKey<unknown>, R = TypeOfBackendTypedQueryKey<K>>(
  options: UndefinedInitialDataTypedOptionsWithListeners<K, R>
) => {
  const { listeners, ...rest } = options;
  const query = useTypedQuery(rest);

  useEffect(() => {
    if (listeners.length < 1 || !query.isFetched) return;

    const removeMessageListeners: (() => void)[] = [];

    listeners.forEach(listener => {
      const removeMessageListener = WebSocketComponent.addMessageListener(listener);
      removeMessageListeners.push(removeMessageListener);
    });

    return () => {
      removeMessageListeners.forEach(removeMessageListener => removeMessageListener());
    };
  }, [listeners, query.isFetched]);

  // TODO: handle a toast if there is an error so we don't have to do it everywhere we use this
  return query;
};

// TODO: this method should set permanent listeners without having to do it manually when we define them, and the method above should not allow permanent listeners
export const usePermanentRealTimeTypedQuery = <K extends TypedPermanentBackendQueryKey<unknown>, R = TypeOfPermanentBackendTypedQueryKey<K>>(
  options: PermanentUndefinedInitialDataTypedOptionsWithListeners<K, R>
) => {
  const { listeners, ...rest } = options;
  const query = useTypedQuery({ ...rest });

  useEffect(() => {
    if (listeners.length < 1 || !query.isFetched) return;

    const removeMessageListeners: (() => void)[] = [];

    listeners.forEach(listener => {
      const removeMessageListener = WebSocketComponent.addMessageListener({ ...listener, permanent: true });
      removeMessageListeners.push(removeMessageListener);
    });

    return () => {
      removeMessageListeners.forEach(removeMessageListener => removeMessageListener());
    };
  }, [listeners, query.isFetched]);

  // TODO: handle a toast if there is an error so we don't have to do it everywhere we use this
  return query;
};
